import { useState, useMemo } from "react"

// Router and context
import { useCurrentUserRoles } from "@/context/user"
import { UserRoles } from "@/constants/constants"

// Components
import { PaymentsList as OwnerPaymentsList } from "./owner/PaymentsList"
import { PaymentsList as SupplierPaymentsList } from "./supplier/PaymentsList"

// Shared
import { FiltersDesktop, FiltersMobile } from "./_components/PaymentsFilters"
import { StateSelectorOptionType } from "./_components/paymentsFilters.constants"

// UI
import { SearchInput } from "@/components/form-controls/Input"
import { CardHeader, CardWrapper, CardBody } from "@/components/Card"
import { Disclaimer } from "@/components/Disclaimer"

// Translations
import { useTrans } from "@/i18n"

// Hooks
import { useTableMetaData } from "@/hooks/useTableMetaData"
import { useScrollToTop } from "@/hooks/useScrollToTop"

/**
 * AllPaymentsOverview
 */
export const AllPaymentsOverview = () => {
	const { hasRole } = useCurrentUserRoles()
	const t = useTrans(["common", "payments"])

	useScrollToTop()

	// Set default selectedStateFilter on mount - default to OPEN
	const initialState = useMemo(() => {
		return {
			selectedStateFilter: "OPEN" as StateSelectorOptionType,
		}
	}, [])

	// State
	const [selectedStateFilter, setSelectedStateFilter] =
		useState<StateSelectorOptionType>(initialState.selectedStateFilter)

	// Shared filter state
	const [filter, setFilter] = useState("")

	// Separate table metadata for owner and supplier views
	const ownerTable = useTableMetaData({ initialSort: "-interestperiod__end" })
	const supplierTable = useTableMetaData({
		initialSort: "-interestperiod__end",
	})

	return (
		<div className="space-y-5 md:space-y-6 lg:space-y-8">
			<title>
				{t("common:web.title", {
					page: t("payments:title"),
				})}
			</title>
			<CardHeader>
				{/* header and search */}
				<div className="flex flex-1 flex-col space-y-4 sm:flex-row sm:space-y-0">
					<div className="sm:order-3 sm:ml-auto">
						<SearchInput
							onChange={(evt) =>
								setFilter(evt.currentTarget.value)
							}
							label={t("payments:search.placeholder")}
							className="md:width-auto min-w-full"
						/>
					</div>
					<div className="order-1 flex space-x-4 md:hidden">
						{/* mobile filters */}
						<FiltersMobile
							selectedStateFilter={selectedStateFilter}
							setSelectedStateFilter={setSelectedStateFilter}
						/>
					</div>
					<div className="order-2 hidden space-x-4 md:flex">
						{/* desktop filters */}
						<FiltersDesktop
							selectedStateFilter={selectedStateFilter}
							setSelectedStateFilter={setSelectedStateFilter}
						/>
					</div>
				</div>
			</CardHeader>
			<div className="md:hidden">
				<Disclaimer message={t("common:table.mobile_warning.copy")} />
			</div>

			{/* Project-owner */}
			{hasRole(UserRoles.projectOwner) || hasRole(UserRoles.admin) ? (
				<CardWrapper>
					<CardBody>
						<OwnerPaymentsList
							filter={filter}
							sort={ownerTable.sort}
							page={ownerTable.page}
							pageSize={ownerTable.pageSize}
							setPage={ownerTable.setPage}
							setSort={ownerTable.setSort}
							selectedStateFilter={selectedStateFilter}
						/>
					</CardBody>
				</CardWrapper>
			) : null}

			{/** Energy supplier approval */}
			{hasRole(UserRoles.energyProvider) || hasRole(UserRoles.admin) ? (
				<CardWrapper>
					<CardBody>
						<SupplierPaymentsList
							filter={filter}
							sort={supplierTable.sort}
							page={supplierTable.page}
							pageSize={supplierTable.pageSize}
							setPage={supplierTable.setPage}
							setSort={supplierTable.setSort}
							selectedStateFilter={selectedStateFilter}
						/>
					</CardBody>
				</CardWrapper>
			) : null}
		</div>
	)
}
